// @ts-nocheck
/**
 * 通用 GA4 委托埋点 SDK（与业务 handler 解耦）。
 * 全局：window.AnalyticsTrackCore — createClient / createModuleExposure / pushSubscriptionEvent / …
 */
(function (global) {
  'use strict';

  global.dataLayer = global.dataLayer || [];
  if (!global.gtag) {
    global.gtag = function gtag() {
      global.dataLayer.push(arguments);
    };
  }

  const STORAGE_KEY_DELAYED = 'ga4_delay_report';

  const DEFAULT_OPTIONS = {
    trackEventAttr: 'data-track-event',
    trackParamsAttr: 'data-track-event-parameters',
    searchInputDebounceMs: 1000,
  };

  function detectDevice() {
    const userAgent = navigator.userAgent || navigator.vendor || global.opera;
    if (
      /iPad|Tablet|PlayBook|Silk/i.test(userAgent) ||
      (/\bAndroid\b/i.test(userAgent) && !/\bMobile\b/i.test(userAgent))
    ) {
      return 'Tablet';
    }
    if (/Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      return 'Mobile';
    }
    return 'PC';
  }

  function debounce(fn, waitMs) {
    let timeoutId;
    return function debounced(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), waitMs);
    };
  }

  function parseJsonAttr(element, attrName) {
    const raw = element?.getAttribute(attrName);
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  function defaultGetUserContext() {
    const title = document.title;
    return {
      operating_platform: detectDevice(),
      pathname: global.location.pathname,
      content_group: title,
      page_title: title,
      page_location: global.location.href,
    };
  }

  /**
   * 邮件订阅等：直接写入 dataLayer（供各页面脚本调用）。
   * @param {{ module?: string, buttonName?: string, button_name?: string, status?: string }} params
   */
  function pushSubscriptionEvent(params) {
    if (!params || typeof params !== 'object') return;
    const module = params.module;
    const buttonName = params.buttonName ?? params.button_name;
    const status = params.status;
    const event_parameters = {};
    if (module != null && String(module).trim() !== '') {
      event_parameters.module = String(module).trim();
    }
    if (buttonName != null && String(buttonName).trim() !== '') {
      event_parameters.button_name = String(buttonName).trim();
    }
    if (status != null && String(status).trim() !== '') {
      event_parameters.status = String(status).trim();
    }
    if (Object.keys(event_parameters).length === 0) return;
    global.dataLayer.push({
      event: 'ga4Event',
      event_name: 'subscription',
      event_parameters,
    });
  }

  /**
   * 模块曝光：≥ threshold 面积进入视口且连续保持 ≥ minVisibleMs 记一次；单次 page_view 内同一 moduleId 仅一次（内存 Set，刷新页面重置）。
   * 用法类似业务侧 createHandlers：传入 onExpose 中再调 publish / dataLayer。
   *
   * @param {{
   *   selector?: string,
   *   idAttr?: string,
   *   paramsAttr?: string,
   *   threshold?: number,
   *   minVisibleMs?: number,
   *   root?: Element | null,
   *   rootMargin?: string,
   *   onExpose: (detail: {
   *     element: Element,
   *     moduleId: string,
   *     params: Record<string, unknown>,
   *     entry: IntersectionObserverEntry,
   *   }) => void,
   * }} options
   * @returns {{ start: () => void, refresh: () => void, stop: () => void, resetSeen: () => void }}
   */
  function createModuleExposure(options) {
    if (!options || typeof options.onExpose !== 'function') {
      throw new Error(
        '[AnalyticsTrackCore] createModuleExposure: onExpose is required',
      );
    }

    const {
      selector = '[data-module-exposure]',
      idAttr = 'data-module-exposure',
      paramsAttr = 'data-track-event-parameters',
      threshold = 0.5,
      minVisibleMs = 1000,
      root = null,
      rootMargin = '0px',
      onExpose,
    } = options;

    /** 本页已曝光过的 moduleId（刷新即清空） */
    const impressedModuleIds = new Set();
    /** 元素 -> 待触发的曝光计时器 */
    const pendingTimers = new Map();

    let observer = null;

    function getModuleId(el) {
      return (el.getAttribute(idAttr) || '').trim();
    }

    function clearTimer(el) {
      const tid = pendingTimers.get(el);
      if (tid != null) {
        clearTimeout(tid);
        pendingTimers.delete(el);
      }
    }

    function handleEntries(entries) {
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const el = entry.target;
        const moduleId = getModuleId(el);
        if (!moduleId || impressedModuleIds.has(moduleId)) continue;

        if (entry.intersectionRatio >= threshold) {
          // 保持 ≥ threshold 期间勿重复重置计时器（避免 IO 多次回调把 1s 不断打断）
          if (pendingTimers.has(el)) continue;
          const tid = setTimeout(() => {
            pendingTimers.delete(el);
            if (impressedModuleIds.has(moduleId)) return;
            impressedModuleIds.add(moduleId);
            const params = parseJsonAttr(el, paramsAttr);
            try {
              onExpose({ element: el, moduleId, params, entry });
            } catch (err) {
              console.error(
                '[AnalyticsTrackCore] createModuleExposure onExpose failed:',
                err,
              );
            }
            if (observer) observer.unobserve(el);
          }, minVisibleMs);
          pendingTimers.set(el, tid);
        } else {
          clearTimer(el);
        }
      }
    }

    function buildThresholdList() {
      const t = threshold;
      const set = new Set([0, Math.min(1, Math.max(0, t)), 1]);
      return Array.from(set).sort((a, b) => a - b);
    }

    function ensureObserver() {
      if (observer) return;
      observer = new IntersectionObserver(handleEntries, {
        root: root || null,
        rootMargin,
        threshold: buildThresholdList(),
      });
    }

    function observeEl(el) {
      if (!(el instanceof Element)) return;
      const moduleId = getModuleId(el);
      if (!moduleId || impressedModuleIds.has(moduleId)) return;
      ensureObserver();
      observer.observe(el);
    }

    function start() {
      document.querySelectorAll(selector).forEach(observeEl);
    }

    function refresh() {
      document.querySelectorAll(selector).forEach(observeEl);
    }

    function stop() {
      pendingTimers.forEach((tid) => clearTimeout(tid));
      pendingTimers.clear();
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    /** 仅清空「已曝光」集合（SPA 换路由时可调；整页刷新无需调） */
    function resetSeen() {
      impressedModuleIds.clear();
    }

    return {
      start,
      refresh,
      stop,
      resetSeen,
    };
  }

  function createPublish(getUserContext) {
    function setDelayedReport(data) {
      const base = getUserContext();
      try {
        sessionStorage.setItem(
          STORAGE_KEY_DELAYED,
          JSON.stringify({
            ...data,
            event: 'delayed_ga4Event',
            common_delayed_data: {
              ...base,
              page_referrer: document.referrer,
            },
          }),
        );
      } catch (error) {
        console.warn('[AnalyticsTrackCore] setDelayedReport failed:', error);
      }
    }

    function publish(payload) {
      if (typeof Shopify === 'undefined' || !Shopify.analytics?.publish) return;
      const { use_delay_report = false, ...rest } = payload;
      const { log_name, event_name, event_parameters } = rest;
      if (use_delay_report) {
        setDelayedReport(rest);
      } else {
        Shopify.analytics.publish('ga4Event', {
          log_name,
          event_name,
          event_parameters,
        });
      }
    }

    return { publish };
  }

  /**
   * @typedef {{ event: Event, element: Element, params: Record<string, unknown> }} TrackContext
   * @typedef {(ctx: TrackContext) => void} TrackHandler
   * @typedef {{ publish: (p: object) => void, getUserContext: () => object }} HandlerDeps
   * @typedef {(deps: HandlerDeps) => Record<string, TrackHandler>} CreateHandlers
   */

  /**
   * @param {object} userOptions
   * @param {() => object} [userOptions.getUserContext] 默认 minimal 上下文
   * @param {CreateHandlers} [userOptions.createHandlers] 返回 data-track-event 名 -> 处理函数
   */
  function createClient(userOptions) {
    const options = { ...DEFAULT_OPTIONS, ...userOptions };
    const {
      trackEventAttr,
      trackParamsAttr,
      searchInputDebounceMs,
      getUserContext: getUserContextOpt,
      createHandlers,
    } = options;

    const getUserContext =
      typeof getUserContextOpt === 'function'
        ? getUserContextOpt
        : defaultGetUserContext;

    const { publish } = createPublish(getUserContext);

    function buildHandlers() {
      if (typeof createHandlers === 'function') {
        return createHandlers({ publish, getUserContext });
      }
      return {};
    }

    let handlers = buildHandlers();
    let inited = false;
    /** @type {(e: MouseEvent) => void} */
    let onClick;
    /** @type {(e: Event) => void} */
    let onInput;

    function getHandler(name) {
      return handlers[name];
    }

    function registerHandler(name, fn) {
      if (typeof name !== 'string' || typeof fn !== 'function') return;
      handlers[name] = fn;
    }

    function resetHandlers() {
      handlers = buildHandlers();
    }

    function init() {
      if (inited) return;

      onClick = (e) => {
        const el = e.target.closest(`[${trackEventAttr}]`);
        if (!el) return;

        const name = el.getAttribute(trackEventAttr);
        if (!name || (name === 'search' && el.tagName === 'INPUT')) return;

        const handler = getHandler(name);
        if (!handler) return;

        const params = parseJsonAttr(el, trackParamsAttr);
        try {
          handler({ event: e, element: el, params });
        } catch (err) {
          console.error('[AnalyticsTrackCore] handler failed:', name, err);
        }
      };

      onInput = debounce((e) => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement)) return;
        // 仅带 data-track-event="search" 的输入框才走搜索埋点（勿用 tagName 误放行全站 input）
        if (target.getAttribute(trackEventAttr) !== 'search') return;
        const params = parseJsonAttr(target, trackParamsAttr);
        const searchHandler = getHandler('search');
        if (searchHandler) {
          try {
            searchHandler({ event: e, element: target, params });
          } catch (err) {
            console.error('[AnalyticsTrackCore] search handler failed:', err);
          }
        }
      }, searchInputDebounceMs);

      document.addEventListener('click', onClick);
      document.addEventListener('input', onInput);
      inited = true;
    }

    function destroy() {
      if (!inited) return;
      if (onClick) document.removeEventListener('click', onClick);
      if (onInput) document.removeEventListener('input', onInput);
      onClick = undefined;
      onInput = undefined;
      inited = false;
    }

    return {
      init,
      destroy,
      registerHandler,
      resetHandlers,
      publish,
      getUserContext,
    };
  }

  global.AnalyticsTrackCore = {
    createClient,
    createModuleExposure,
    pushSubscriptionEvent,
    detectDevice,
    debounce,
    parseJsonAttr,
    defaultGetUserContext,
  };

  /** 简短别名，便于页面内联脚本调用 */
  global.pushSubscriptionGa4Event = pushSubscriptionEvent;
})(typeof window !== 'undefined' ? window : this);
